import KlassesHeaderCell from './KlassesHeaderCell';
import KlassesIconCell from './KlassesIconCell';
import GivenContent from './GivenContent';
import ScheduledLesson from '@/models/ScheduledLesson';
import { isLessonBeforeToday } from '@/utils/ScheduledLessonDateValidator';
import Klass from '@/models/Klass';
import Enrollment, { EnrollmentStatusEnum } from '@/models/Enrollment';
import ScheduledLessonReport, {
  ScheduledLessonReportEnum,
} from '@/models/ScheduledLessonReport';
import User from '@/models/User';
import { LessonProgressFilters } from '@/data/services/lessonProgressServices';
import useListService from '@/data/hook/useListService';
import { REQUEST_STALE_TIME_IN_MS as staleTime } from '@/constants';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { useQuery } from '@tanstack/react-query';
import {
  scheduledLessonReportsQueryKeys,
  simpleLessonsProgressesQueryKeys,
  usersQueryKeys,
  vacationNoticeQueryKeys,
} from '@/data/services/querykeys';
import moment from 'moment';
import { sortEnrollmentsById } from '@/utils/sortEnrollmentsById';

interface KlassesLessonColProps {
  scheduledLesson: ScheduledLesson;
  klass: Klass;
  enrollments: Enrollment[];
  teacher?: User;
}

export default function KlassesLessonCol(props: KlassesLessonColProps) {
  const { scheduledLesson, enrollments, klass, teacher } = props;

  const courseSlug = klass.coursePathSlug;
  const isBeforeToday = isLessonBeforeToday(scheduledLesson);
  const isUpcoming = !isBeforeToday && !scheduledLesson.hasDone;

  const { data: newTeacher } = useQuery({
    ...usersQueryKeys.get(scheduledLesson.teacher),
    enabled: teacher?.id !== scheduledLesson.teacher && !isUpcoming,
  });

  const klassId = klass.id;
  const studentIds = enrollments.map(enroll => enroll.student.id);
  const scheduledLessonId = scheduledLesson.id;

  const filters: LessonProgressFilters = {
    lesson: [scheduledLesson.lesson.id],
    klassId,
    studentId: studentIds,
  };

  const enabled = !!enrollments.length;

  const {
    results: simpleLessonProgress,
    isLoading: loadingSimpleLessonProgress,
  } = useListService({
    staleTime,
    enabled,
    ...simpleLessonsProgressesQueryKeys.list(filters),
  });

  const { results: reports, isLoading: loadingReports } = useListService({
    staleTime,
    enabled,
    ...scheduledLessonReportsQueryKeys.list({
      studentId: studentIds,
      scheduledLesson: scheduledLessonId,
    }),
  });

  const { results: vacationNotices } = useListService({
    staleTime,
    enabled: !!enrollments.length,
    ...vacationNoticeQueryKeys.list({
      userId: studentIds,
      date: moment(scheduledLesson.datetime).format('YYYY-MM-DD'),
    }),
  });

  const getProgressInfo = (
    status: EnrollmentStatusEnum,
    report?: ScheduledLessonReport,
  ) => {
    const disabled = status !== EnrollmentStatusEnum.ACTIVE;

    const differentCourseAndLesson =
      status === EnrollmentStatusEnum.COURSE_TRANSFERRED &&
      !report?.presence &&
      report?.status !== ScheduledLessonReportEnum.TRANSFERED;

    const isCancelledNoPresence =
      report?.status === ScheduledLessonReportEnum.CANCELED &&
      !report?.presence;

    const hideIconCell = isCancelledNoPresence || differentCourseAndLesson;

    return { disabled, hideIconCell };
  };

  return (
    <div
      data-testid={`klassesLessonCol${scheduledLesson.order}`}
      key={scheduledLesson.id}
      className="flex flex-col h-full grow border-r border-neutral-content"
    >
      <KlassesHeaderCell
        scheduledLesson={scheduledLesson}
        courseSlug={courseSlug}
      />
      <ConditionalRenderer
        condition={!!enrollments.length}
        fallback={
          <div className="h-14 border-b border-neutral-content flex shrink-0" />
        }
      >
        {sortEnrollmentsById(enrollments).map(enrollment => {
          const student = enrollment.student;

          const progress = simpleLessonProgress.find(
            progress => progress.studentId === student.id,
          );

          const report = reports.find(report => report.student === student.id);

          const { disabled, hideIconCell } = getProgressInfo(
            enrollment.status,
            report,
          );

          const vacationNotice = vacationNotices.find(
            vacation => vacation.userId === student.id,
          );

          return (
            <KlassesIconCell
              key={student.id}
              loading={loadingSimpleLessonProgress || loadingReports}
              disabled={!progress || disabled}
              scheduledLesson={scheduledLesson}
              vacationNotice={vacationNotice}
              progress={hideIconCell ? undefined : progress}
            />
          );
        })}
      </ConditionalRenderer>

      <GivenContent
        scheduledLesson={scheduledLesson}
        teacher={newTeacher}
        klassId={klass?.id || 0}
      />
    </div>
  );
}
